import React, { useEffect, useMemo, useState } from "react";

import _ from "lodash";
import cx from "classnames";
import dateFormat from "dateformat";

import { useDispatch } from "react-redux";
import Tooltip from "src/components/tooltip";
import {
  createCourse,
  fetchDraftCourses,
  fetchPublishedCourses,
  invalidateCourseCreationState,
  invalidateSSESearchResults,
  searchCoursesSSESubscribe,
} from "src/redux/actions/coaching/courses";
import { DeleteCourseDialog } from "./courses/edit/components/menu";
import { AddLearnersDialog } from "./courses/edit/components/learners/add";
import { useSelector } from "react-redux";
import InfiniteScroll from "src/components/infiniteScroll";
import { Course, CourseSummary, DraftCourseSummary, PublishedCourseSummary } from "src/types/coaching/courses";
import LinesEllipsis from "react-lines-ellipsis";
import { BannerView } from "src/components/banner";
import { Link, useNavigate } from "react-router-dom";

import styles from "./course.module.scss";
import pluralize from "pluralize";

import { FileService } from "src/utils/file.service";
import CreateSimulationDialog from "./simulations/components/simulationTypeDialog";
import { createSimulation, invalidateSimulationCreationState } from "src/redux/actions/coaching/simulation";
import { CALL_TYPES } from "src/constants/simulation";
import { Simulation, SimulationSummary } from "src/types/coaching/simulation";
import { Podcast } from "src/types/coaching/podcast";
import { DeleteSimulationDialog } from "./simulations/components/menu";
import { DeletePodcastDialog } from "./podcasts/components/menu";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { useTransition, animated } from "@react-spring/web";
import Text from "src/components/text";

export const ManageCoursesPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searching = useMemo(() => {
    return (query?.length || 0) > 0;
  }, [query]);


  const coursesTransition = useTransition(!searching, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const searchTransition = useTransition(searching, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleSearch = (query: string) => {
    setQuery(query);
  }

  const handleSearchResults = (show: boolean) => {
    setShowSearchResults(show);
  }

  if (!userRoleService.canCreateCourse()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  return (
    <div className={styles.courseContainer}>
      <CourseHeader onSearch={handleSearch} onShowSearchResults={handleSearchResults} />

      <div className={styles.coursesViewContainer}>
        {coursesTransition(
          (style, item) =>
            item && (
              <animated.div style={style} className={styles.courseTypeContainer}>
                <PublishedCoursesContainer />
                <DraftCoursesContainer />
              </animated.div>
            )
        )}

        {searchTransition(
          (style, item) =>
            item && (
              <animated.div style={style} className={styles.searchCourseContainer}>
                <SearchCoursesContainer query={query} showSearchResults={showSearchResults} />
              </animated.div>
            )
        )}
      </div>
    </div>
  );
};

interface CourseHeaderProps {
  // eslint-disable-next-line no-unused-vars
  onSearch: (query: string) => void;
  // eslint-disable-next-line no-unused-vars
  onShowSearchResults: (show: boolean) => void;
}

const CourseHeader: React.FC<CourseHeaderProps> = ({ onSearch, onShowSearchResults }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = React.useState<string>("");

  const { creating, createdCourse } = useSelector((state) => {
    const creating = _.get(state, 'courses.creation.creating');
    const createdCourse = _.get(state, 'courses.creation.item');

    return {
      creating,
      createdCourse
    };
  }, _.isEqual);

  const {thinking, message} = useSelector((state => {
    const thinking = _.get(state, 'courses.search.loading');
    const message = _.get(state, 'courses.search.message', null);

    return {
      thinking,
      message
    }
  }
  ), _.isEqual);


  const handleCreateCourse = () => {
    dispatch(createCourse({
      name: 'Untitled Course',
    }));
  }

  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setQuery(value);

    if(!value){
      onSearch(value);
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      return;
    }
    onSearch(query);
  }

  const handleClearSearch = () => {
    setQuery("");
    onSearch("");
    onShowSearchResults(false);
  }

  useEffect(() => {
    if (createdCourse) {
      const id = _.get(createdCourse, 'id');
      dispatch(invalidateCourseCreationState());
      navigate(`/coaching/manage/courses/draft/${id}`);
    }
  }, [createdCourse]);

  useEffect(() => {
    if(query.length === 0) {
      dispatch(invalidateSSESearchResults());
      onShowSearchResults(false);
    }
  }, [query]);

  return (
    <div>
      <div className={styles.createCourseContainer}>
        <form className={styles.searchForm} onSubmit={handleSearch}>
          <textarea className={styles.input}
            ref={textareaRef}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSearch(e);
              }
            }}
            placeholder="Ask for any course, skills or topics of your choice. You can also describe a situation you’d like to learn about."
            value={query}
            onChange={handleQueryChange}
          />
          {query.length === 0 && (
          <button className={styles.searchButton} type="submit">
            <i className={cx(styles.icon, "paperflite-search")}></i>
          </button>
          )}

          {query.length > 0 && (
          <button className={styles.clearButton} onClick={handleClearSearch}>
            <i className={cx(styles.icon, "paperflite-close")}></i>
          </button>
          )}
        </form>
        <button
          className={cx(styles.create, {
            [styles.creating]: creating,
          })}
          disabled={creating}
          onClick={handleCreateCourse}
          data-testid="create-course-button"
        >
          <div className={styles.top}>
            <i className={cx(styles.icon, "paperflite-add")}></i>
            <span className={styles.text}>
              {creating ? "creating course..." : "create new course"}
            </span>
          </div>
          <p className={styles.bottom}>
            build learning paths with lessons and assessments to track team
            success
          </p>
        </button>
      </div>
      <div className={styles.typewriterContainer}>
        {thinking && !message && query.length > 0  && <ThinkingIndicator />}
        {!thinking && message && query.length > 0 && (
          <Typewriter text={message} onComplete={() => onShowSearchResults(true)} />
        )}
      </div>
    </div>
  );
};

const PublishedCoursesContainer = () => {
  const dispatch = useDispatch();
  const limit = 25;

  const { courses, loading, hasMoreItems, nextPage, remaining } = useSelector((state) => {
    const coursesState = _.get(state, 'courses.published');

    const courses = _.get(coursesState, 'items') || [];
    const loading = _.get(coursesState, 'loading');
    const pagination = _.get(coursesState, 'pagination');
    const total = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let remaining = total - _.size(courses);
    if (remaining > limit) {
      remaining = limit;
    }

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      courses,
      loading,
      hasMoreItems,
      nextPage,
      remaining
    };
  }, _.isEqual);

  const [action, setAction] = React.useState<{
    type: string;
    course: PublishedCourseSummary | null;
  }>({
    type: "",
    course: null,
  });

  useEffect(() => {
    fetchPublishedCoursesForPage(0);
  }, []);

  const fetchPublishedCoursesForPage = (page) => {
    dispatch(fetchPublishedCourses({
      page,
      limit: limit
    }));
  }

  const handleLoadMorePublishedCourses = () => {
    if (nextPage) {
      fetchPublishedCoursesForPage(nextPage);
    }
  };

  const handleAddLearnersClick = (course: PublishedCourseSummary) => {
    setAction({
      type: "addLearners",
      course: course,
    });
  }

  const handleAddLearnersFinished = () => {
    resetAction();
  };

  const handleDeleteClick = (course: PublishedCourseSummary) => {
    if (course.entityType === 'simulation') {
      setAction({
        type: "delete-simulation",
        course: course,
      });
    } else if (course.entityType === 'course') {
      setAction({
        type: "delete-course",
        course: course,
      });
    } else if (course.entityType === 'podcast') {
      setAction({
        type: "delete-podcast",
        course: course,
      });
    }
  };

  const handleDeleteFinish = () => {
    resetAction();
  }

  const resetAction = () => {
    setAction({
      type: "",
      course: null,
    });
  };

  return (
    <div className={styles.publishedCourseList}>
      <h6 className={styles.title}>your course library: published and ready</h6>

      <div className={styles.courseList}>
        {courses.map((course: PublishedCourseSummary) => (
          <PublishedCourseCard key={course.id}
            course={course}
            onDelete={handleDeleteClick}
            onAddLearners={handleAddLearnersClick}
          />
        ))}

        {loading &&
          _.times(_.size(courses) == 0 ? 4 : 1).map((i) => (
            <PlaceholderPublishedCourseCard key={i} />
          ))}
      </div>

      {hasMoreItems && !loading && (
        <button
          className={styles.showMore}
          onClick={handleLoadMorePublishedCourses}
        >
          <i className={cx(styles.icon, "paperflite-dropdown")}></i>
          show more ({remaining})
        </button>
      )}

      <DeleteCourseDialog
        show={action.type === "delete-course"}
        course={action.course?.course as Course}
        onFinish={handleDeleteFinish}
        onClose={() => resetAction()}
      />

      <DeleteSimulationDialog
        show={action.type === "delete-simulation"}
        simulation={action.course?.course as Simulation}
        onFinish={handleDeleteFinish}
        onClose={() => resetAction()}
      />

      <DeletePodcastDialog 
        show={action.type === "delete-podcast"}
        podcast={action.course?.course as Podcast}
        onFinish={handleDeleteFinish}
        onClose={() => resetAction()}
      />

      <AddLearnersDialog
        show={action.type === "addLearners"}
        course={action.course?.course as Course}
        onFinish={handleAddLearnersFinished}
        onClose={() => resetAction()}
      />
    </div>
  );
}

const DraftCoursesContainer = () => {
  const dispatch = useDispatch();

  const { courses, loading, hasMoreItems, nextPage } = useSelector((state) => {
    const coursesState = _.get(state, 'courses.drafts');

    const courses = _.get(coursesState, 'items') || [];
    const loading = _.get(coursesState, 'loading');
    const pagination = _.get(coursesState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      courses,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);

  useEffect(() => {
    fetchDraftCoursesForPage(0);
  }, []);

  const fetchDraftCoursesForPage = (page) => {
    dispatch(fetchDraftCourses({
      page,
      limit: 20
    }));
  }

  const handleLoadMoreDraftCourses = () => {
    if (nextPage) {
      fetchDraftCoursesForPage(nextPage);
    }
  };

  return (
    <div className={styles.draftCourseList}>
      <h6 className={styles.title}>your course vault: drafts in progress</h6>

      <InfiniteScroll
        className={styles.courseList}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreDraftCourses}
        loader={() => {
          return _.times(_.size(courses) == 0 ? 4 : 1).map((i) => (
            <PlaceholderDraftCourseCard key={i} />
          ));
        }}
      >
        {courses.map((course: any) => (
          <DraftCourseCard key={course.id} course={course} />
        ))}
      </InfiniteScroll>
    </div>
  );
};

interface PublishedCourseCardProps {
  course: PublishedCourseSummary;

  // eslint-disable-next-line no-unused-vars
  onDelete: (course: PublishedCourseSummary) => void;

  // eslint-disable-next-line no-unused-vars
  onAddLearners: (course: PublishedCourseSummary) => void;
}

const PublishedCourseCard: React.FC<PublishedCourseCardProps> = ({
  course, onDelete, onAddLearners
}) => {
  const navigate = useNavigate();

  const [showTooltip, setShowTooltip] = React.useState(false);

  const callType = useMemo(() => {
    const value = _.get(course, "course.callType") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [course]);

  const duration = useMemo(() => _.get(course, 'course.duration') || 0, [course]);

  const courseUrl = useMemo(() => {
    const courseId = _.get(course, "course.id");
    const courseType = _.get(course, "entityType");
    if (courseId && courseType === 'course') {
      return `/coaching/manage/courses/draft/${courseId}`;
    } else if (courseId && courseType === 'simulation') {
      return `/coaching/manage/simulations/draft/${courseId}`;
    } else if (courseId && courseType === 'podcast') {
      return `/coaching/manage/podcasts/draft/${courseId}`;
    }

    return "";
  }, [course]);

  return (
    <div className={cx(styles.publishedCourseCard)}>
      <div className={styles.top}>
        <div className={styles.icon}>
          <BannerView banner={course.course?.banner as any} height={100} defaultBanner="/images/assets/default_asset.jpg" />
        </div>
        <div className={styles.details}>
          <Link
            to={courseUrl}
            className={styles.name}
          >
          <Text text={course?.course?.name || ""} lines={1}  />            
          
          </Link>
          <div className={styles.metadata}>
            {course.entityType === 'course' && (
              <>
                <span className={styles.value}>Course: </span>
                <span className={styles.value}>
                  <i className="paperflite-document" />
                  {`${(course.course as CourseSummary)?.lessons} ${pluralize("Lessons", (course.course as CourseSummary)?.lessons)}`}
                </span>
                <span className={styles.divider}></span>
                <span className={styles.value}>
                  <i className="paperflite-document" />
                  {`${(course.course as CourseSummary)?.assessments} ${pluralize("Assessments", (course.course as CourseSummary)?.assessments)}`}
                </span>
                <span className={styles.divider}></span>
                <span className={styles.value}>
                  <i className="paperflite-clock" />
                  Updated on {dateFormat(course.course?.modifiedDate, 'dd mmm yyyy')}
                </span>
              </>
            )}

            {course.entityType === 'simulation' && (
              <>
                <span className={styles.value}>Simulation: </span>
                {duration > 0 && (
                  <>
                    <div className={styles.value}>
                      <i className="paperflite-clock"></i>
                      <span>{duration} Minutes</span>
                    </div>
                    <div className={styles.divider} />
                  </>
                )}
                <div className={styles.value}>
                  <i className="paperflite-rocket"></i>
                  <span>{callType}</span>
                </div>
                <div className={styles.divider} />
                <div className={styles.value}>
                  <i className="paperflite-clock" />
                  Updated on {dateFormat(course.course?.modifiedDate, 'dd mmm yyyy')}
                </div>
              </>
            )}

            {course.entityType === 'podcast' && (
              <>
                <span className={styles.value}>Podcast: </span>
                {duration > 0 && (
                  <>
                    <div className={styles.value}>
                      <i className="paperflite-clock"></i>
                      <span>{FileService.formatDuration(duration, 'long')}</span>
                    </div>
                    <div className={styles.divider} />
                  </>
                )}
                <div className={styles.value}>
                  <i className="paperflite-clock" />
                  Updated on {dateFormat(course.course?.modifiedDate, 'dd mmm yyyy')}
                </div>
              </>
            )}
          </div>
        </div>
        <div className={styles.analytics}>
          <div className={styles.analytic}>
            <span className={styles.value}>{course.completed}</span>
            <span className={styles.label}>Completed</span>
          </div>
          <div className={styles.analytic}>
            <span className={styles.value}>{course.inProgress}</span>
            <span className={styles.label}>In-progress</span>
          </div>
          <div className={styles.analytic}>
            <span className={styles.value}>{course.notStarted}</span>
            <span className={styles.label}>Not started</span>
          </div>
          <div className={styles.analytic}>
            <span className={styles.value}>{FileService.formatDuration(course.duration, 'short')}</span>
            <span className={styles.label}>Total time spent</span>
          </div>
        </div>
        <div className={styles.actions}>
          <button className={styles.action} onClick={() => navigate(courseUrl)}>
            <i className="paperflite-arrow-up"></i>
          </button>
        </div>
      </div>
      <div className={styles.actions}>
        <button className={cx(styles.action, styles.assign)} onClick={() => onAddLearners(course)}>
          <i className="paperflite-user-settings-property"></i>
          <span>assign learners</span>
        </button>
        <button
          className={cx(styles.action, styles.delete)}
          onClick={() => onDelete(course)}
        >
          <i className="paperflite-delete"></i>
        </button>
      </div>
    </div>
  );
};

interface DraftCourseCardProps {
  course: DraftCourseSummary;
}

const DraftCourseCard: React.FC<DraftCourseCardProps> = ({ course }) => {
  const description = useMemo(() => {
    const description = (_.get(course, "course.description") || "") as string;
    if (description) {
      const doc = new DOMParser().parseFromString(description, "text/html");
      return doc.body.textContent || "";
    }
    return description;
  }, [course]);

  const courseUrl = useMemo(() => {
    const courseId = _.get(course, "course.id");
    const courseType = _.get(course, "entityType");
    if (courseId && courseType === 'course') {
      return `/coaching/manage/courses/draft/${courseId}`;
    } else if (courseId && courseType === 'simulation') {
      return `/coaching/manage/simulations/draft/${courseId}`;
    } else if (courseId && courseType === 'podcast') {
      return `/coaching/manage/podcasts/draft/${courseId}`;
    }

    return "";
  }, [course]);

  const callType = useMemo(() => {
    const value = _.get(course, "course.callType") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [course]);

  const duration = useMemo(() => _.get(course, 'course.duration') || 0, [course]);

  return (
    <div className={cx(styles.draftCourseCard)}>
      <div className={styles.header}>
        <div className={styles.icon}>
          <BannerView
            banner={course.course?.banner as any}
            height={100}
            defaultBanner="/images/assets/default_asset.jpg"
          />
        </div>
      </div>
      <div className={styles.details}>
        <Link
          to={courseUrl}
          className={styles.name}
        >
         <Text text={course?.course?.name || ""} lines={2} />
          
        </Link>
        <div className={styles.description}>
          <Text text={description || ""} lines={3} />
        </div>
      </div>
      <div className={styles.metadata}>
        {course.entityType === 'course' && (
          <>
            <span className={styles.value}>
              <i className="paperflite-document" />
              {`${(course.course as CourseSummary).lessons} ${pluralize("Lessons", (course.course as CourseSummary).lessons)}`}
            </span>
            <span className={styles.divider}></span>
            <span className={styles.value}>
              <i className="paperflite-document" />
              {`${(course.course as CourseSummary).assessments} ${pluralize("Assessments", (course.course as CourseSummary).assessments)}`}
            </span>
            <span className={styles.divider}></span>
            <span className={styles.value}>
              <i className="paperflite-clock" />
              Updated on {dateFormat(course.course.modifiedDate, 'dd mmm yyyy')}
            </span>
          </>
        )}

        {course.entityType === 'simulation' && (
          <>
            {duration > 0 && (
              <>
                <div className={styles.value}>
                  <i className="paperflite-clock"></i>
                  <span>{duration} Minutes</span>
                </div>
                <div className={styles.divider} />
              </>
            )}
            <div className={styles.value}>
              <i className="paperflite-rocket"></i>
              <span>{callType}</span>
            </div>
            <div className={styles.divider} />
            <div className={styles.value}>
              <i className="paperflite-clock" />
              Updated on {dateFormat(course?.course?.modifiedDate, 'dd mmm yyyy')}
            </div>
          </>
        )}

        {course.entityType === 'podcast' && (
          <>
            {duration > 0 && (
              <>
                <div className={styles.value}>
                  <i className="paperflite-clock"></i>
                  <span>{FileService.formatDuration(duration, 'long')}</span>
                </div>
                <div className={styles.divider} />
              </>
            )}
            <div className={styles.value}>
              <i className="paperflite-clock" />
              Updated on {dateFormat(course?.course?.modifiedDate, 'dd mmm yyyy')}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface SearchCourseCard {
  course: CourseSummary | SimulationSummary;
}

const SearchCourseCard: React.FC<SearchCourseCard> = ({ course }) => {
  const description = useMemo(() => {
    const description = (_.get(course, "description") || "") as string;
    if (description) {
      const doc = new DOMParser().parseFromString(description, "text/html");
      return doc.body.textContent || "";
    }
    return description;
  }, [course]);

  const courseUrl = useMemo(() => {
    const courseId = _.get(course, "id");
    const courseType = _.get(course, "entityType");
    if (courseId && courseType === 'course') {
      return `/coaching/manage/courses/draft/${courseId}`;
    } else if (courseId && courseType === 'simulation') {
      return `/coaching/manage/simulations/draft/${courseId}`;
    } else if (courseId && courseType === 'podcast') {
      return `/coaching/manage/podcasts/draft/${courseId}`;
    }

    return "";
  }, [course]);

  const callType = useMemo(() => {
    const value = _.get(course, "callType") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [course]);

  const duration = useMemo(() => _.get(course, 'duration') || 0, [course]);

  return (
    <div className={cx(styles.draftCourseCard)}>
      <div className={styles.header}>
        <div className={styles.icon}>
          <BannerView
            banner={course?.banner as any}
            height={100}
            overlay={false}
            defaultBanner="/images/assets/default_asset.jpg"
          />
        </div>
      </div>
      <div className={styles.details}>
        <Link
          to={courseUrl}
          className={styles.name}
        >
         <Text text={course?.name || ""} lines={2} />
          
        </Link>
        <div className={styles.description}>
          <Text text={description || ""} lines={3} />
        </div>
      </div>
      <div className={styles.metadata}>
        {course.entityType === 'course' && (
          <>
            <span className={styles.value}>
              <i className="paperflite-document" />
              {`${(course as CourseSummary).lessons} ${pluralize("Lessons", (course as CourseSummary).lessons)}`}
            </span>
            <span className={styles.divider}></span>
            <span className={styles.value}>
              <i className="paperflite-document" />
              {`${(course as CourseSummary).assessments} ${pluralize("Assessments", (course as CourseSummary).assessments)}`}
            </span>
            <span className={styles.divider}></span>
            <span className={styles.value}>
              <i className="paperflite-clock" />
              Updated on {dateFormat(course.modifiedDate, 'dd mmm yyyy')}
            </span>
          </>
        )}

        {course.entityType === 'simulation' && (
          <>
            {duration > 0 && (
              <>
                <div className={styles.value}>
                  <i className="paperflite-clock"></i>
                  <span>{duration} Minutes</span>
                </div>
                <div className={styles.divider} />
              </>
            )}
            <div className={styles.value}>
              <i className="paperflite-rocket"></i>
              <span>{callType}</span>
            </div>
            <div className={styles.divider} />
            <div className={styles.value}>
              <i className="paperflite-clock" />
              Updated on {dateFormat(course?.modifiedDate, 'dd mmm yyyy')}
            </div>
          </>
        )}

        {course.entityType === 'podcast' && (
          <>
            {duration > 0 && (
              <>
                <div className={styles.value}>
                  <i className="paperflite-clock"></i>
                  <span>{FileService.formatDuration(duration, 'long')}</span>
                </div>
                <div className={styles.divider} />
              </>
            )}
            <div className={styles.value}>
              <i className="paperflite-clock" />
              Updated on {dateFormat(course?.modifiedDate, 'dd mmm yyyy')}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PlaceholderDraftCourseCard: React.FC<object> = () => {
  return (
    <div className={cx(styles.draftCourseCard, styles.placeholder)}>
      <div className={styles.header}>
        <div className={cx(styles.icon, styles.block)}></div>
      </div>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)}></div>
        <div className={cx(styles.description)}>
          <span className={styles.block}></span>
          <span className={styles.block}></span>
          <span className={styles.block}></span>
        </div>
      </div>
      <div className={styles.metadata}>
        <div className={cx(styles.value, styles.block)}></div>
        <div className={cx(styles.value, styles.block)}></div>
        <div className={cx(styles.value, styles.block)}></div>
      </div>
    </div>
  );
}

const PlaceholderPublishedCourseCard: React.FC<object> = () => {
  return (
    <div className={cx(styles.publishedCourseCard, styles.placeholder)}>
      <div className={styles.top}>
        <div className={cx(styles.icon, styles.block)}></div>
        <div className={styles.details}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.metadata}>
            <div className={cx(styles.value, styles.block)}></div>
            <div className={cx(styles.value, styles.block)}></div>
            <span className={styles.divider}></span>
            <div className={cx(styles.value, styles.block)}></div>
            <span className={styles.divider}></span>
            <div className={cx(styles.value, styles.block)}></div>
          </div>
        </div>
        <div className={styles.analytics}>
          <div className={styles.analytic}>
            <span className={cx(styles.value, styles.block)}></span>
            <span className={cx(styles.label, styles.block)}></span>
          </div>
          <div className={styles.analytic}>
            <span className={cx(styles.value, styles.block)}></span>
            <span className={cx(styles.label, styles.block)}></span>
          </div>
          <div className={styles.analytic}>
            <span className={cx(styles.value, styles.block)}></span>
            <span className={cx(styles.label, styles.block)}></span>
          </div>
          <div className={styles.analytic}>
            <span className={cx(styles.value, styles.block)}></span>
            <span className={cx(styles.label, styles.block)}></span>
          </div>
        </div>
        <div className={styles.actions}>

        </div>
      </div>
      <div className={styles.actions}>
        <button className={cx(styles.action, styles.assign)}>
          <span className={cx(styles.icon, styles.block)}></span>
          <span className={cx(styles.name, styles.block)}></span>
        </button>
        <button className={cx(styles.action, styles.delete)}>
          <span className={cx(styles.icon, styles.block)}></span>
        </button>
      </div>
    </div>
  );
}

interface SearchCoursesContainerProps {
  query: string;
  showSearchResults: boolean;
}
// Search
const SearchCoursesContainer: React.FC<SearchCoursesContainerProps> = ({ query, showSearchResults }) => {
  const dispatch = useDispatch();

  const { session, courses, loading, hasMoreItems, nextPage } = useSelector((state) => {
    const coursesState = _.get(state, 'courses.search');

    const session = _.get(state, 'courses.search.session', null);
    const courses = _.get(coursesState, 'items') || [];
    const loading = _.get(coursesState, 'loading');
    const pagination = _.get(coursesState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      session,
      courses,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);

  useEffect(() => {
    searchCoursesForPage(0);
  }, []);

  const searchCoursesForPage = (page) => {
    dispatch(searchCoursesSSESubscribe({
      query: query,
      session: session,
      page: page,
      limit: 10
    }));
  }

  const handleLoadMoreSearchCourses = () => {
    if (nextPage) {
      searchCoursesForPage(nextPage);
    }
  };

  if(!loading && _.size(courses) === 0 && _.size(query) > 0 && showSearchResults) {
    return (
      <div className={styles.noResults}>
        <h6 className={styles.heading}>no learnings <br /> found</h6>
        <p className={styles.message}>Try searching for something else</p>
      </div>
    )
  }  

  return (
    <div className={styles.searchCourseList}>
      <h6 className={styles.title}>Explore the results below</h6>

      <InfiniteScroll
        className={styles.courseList}
        loading={loading || !showSearchResults}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreSearchCourses}
        loader={() => {
          return _.times((_.size(courses) == 0 || !showSearchResults) ? 4 : 1).map((i) => (
            <PlaceholderDraftCourseCard key={i} />
          ));
        }}
      >
        {showSearchResults && courses.map((item: any) => {
          if (item.entityType === 'course') {
            return <SearchCourseCard key={item.id} course={item.course} />;
          }

          if (item.entityType === 'simulation') {
            return <SearchCourseCard key={item.id} course={item.simulation} />;
          }

          if (item.entityType === 'podcast') {
            return <SearchCourseCard key={item.id} course={item.podcast} />;
          }
        })}
      </InfiniteScroll>
    </div>
  );
};

const ThinkingIndicator = () => {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length < 3 ? prev + '.' : ''));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return <span className={styles.indicator}>Thinking{dots}</span>;
};

interface TypewriterProps {
  text: string;
  onComplete?: () => void;
  typingSpeed?: number; // ms per character
}

export const Typewriter = ({ text, onComplete, typingSpeed = 10 }: TypewriterProps) => {
  const [displayedText, setDisplayedText] = useState<string>("");

  useEffect(() => {
    if (!text) {
      setDisplayedText("");
      return;
    }
    let index = 0;
    // setDisplayedText(""); // Reset before typing
    const interval = setInterval(() => {
      index++;
      setDisplayedText(text.slice(0, index));
      if (index >= text.length) {
        clearInterval(interval);
        onComplete?.();
      }
    }, typingSpeed);

    return () => clearInterval(interval);
  }, [text]);

  return <span className={styles.message}>{displayedText}</span>;
};
